import React, { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import ChartPie from './pie';
import ChartBar from './bar';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeUnits, useRealtimeTags } from 'Core/hooks';
import { Loading } from 'Components';
import { useIntl } from 'react-intl';
import { chartDashboardPanel } from 'Redux/actions';
import './index.scss';
import { backgroundColor } from './options/bgColor';
import { getUnitsNaturalOrder } from "Core/data/Helpers";

const DATE_FORMAT = [
  { id: "m/d/Y", name: "MM/DD/YYYY" },
  { id: "d/m/Y", name: "DD/MM/YYYY" },
  { id: "Y/m/d", name: "YYYY/MM/DD" },
];

const Chart = ({ item, update }) => {

    const dispatch = useDispatch();
    const { messages, locale } = useIntl();
    const [typeChart, setTypeChart] = useState('');
    const [data, setData] = useState({});
    const [itemsChartData, setItemsChartData] = useState(null)

    const keyLang = 'dashboardPanel_edit_'

    const { charts_data } = useSelector(state => state.gadgetChartRedux);
    const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
    const { updateResponse } = useSelector((state) => state.dashboardPanelRedux);

    const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
    const unitDashboard = useMemo(() => {
      const orderedUnits = getUnitsNaturalOrder(units);
      return orderedUnits.map(unit => ({
        id: unit?.id,
        label: unit?.label
      }));
    }, [units]);

    const { user: { dateformat } } = useSelector((state) => state.securityRedux);


    useEffect(() => {
            if (updateResponse && updateResponse.id == item.id) {
                dispatch(chartDashboardPanel({ id: parseInt(item.id) }));
                setData({});
            }
    }, [updateResponse])


    useEffect(() => {
        const refresh = update > 0;
        setTimeout(() => { dispatch(chartDashboardPanel({ id: parseInt(item.id), refresh })) }, item?.columnOrder);
        setData({});
    }, [update]);

    useEffect(() => {
        if(charts_data?.id == item.id) {
            setItemsChartData(charts_data.data[0]);
        }
    }, [charts_data]);

    useEffect(() => {
        if (itemsChartData){
            let settings = JSON.parse(item.settings);
            if (settings && (charts_data?.data?.length > 0)) {
                setTypeChart(settings.chartType.id);
                //date Format for preference
                let dateFormatName = DATE_FORMAT.find((date) => date.id === dateformat).name;
                const dateStart = (dateFormatName) ? moment(itemsChartData.start).format(dateFormatName) : itemsChartData.start;
                const dateEnd = (dateFormatName) ? moment(itemsChartData.end).format(dateFormatName) : itemsChartData.end;
                let label = tags?.find(group => group?.id === itemsChartData?.group)?.label || '';
                let legend = false;

                let deviceDataSet = [{
                    data: itemsChartData.data,
                    devices: itemsChartData?.devices,
                    label,
                    backgroundColor
                }];

                //If view is selected
                if (settings?.groupUnit?.id === "compareGroup") {
                  const labelName = tags?.find(group => group?.id === charts_data?.data[1]?.group)?.label || '';
                  label = `${label} / ${labelName}`;
                    deviceDataSet.push({
                        label: labelName,
                        data: charts_data?.data[1]?.data,
                        devices: charts_data?.data[1]?.devices,
                        backgroundColor
                    });
                } else if (settings?.chartType?.id == 'column' && settings?.View?.id) {
                    //If view is selected
                    deviceDataSet = viewData(itemsChartData.devices, settings)
                    legend = true;
                }


                //If percentage is activated
                if (settings?.percentage) {
                    deviceDataSet = percentageData(deviceDataSet, settings);
                }

                //If Average is activated
                if (settings?.chartType.id == 'column' && settings?.average) {
                    deviceDataSet = averageData(deviceDataSet, messages);
                }


                setData({
                    labels: translatorLabel(itemsChartData.label, messages),
                    title: `${messages[keyLang + settings.groupUnit.id]} ${label} `,
                    subTitle: `${messages[keyLang + settings.reportType.id]} ${dateStart} ${messages['dashboardPanel_to']} ${dateEnd}`,
                    unit: translateUnitLabel(itemsChartData.unit, messages),
                    legend,
                    data:itemsChartData?.data,
                    backgrounds:backgroundColor,
                    show_labels: settings?.show_labels,
                    percentage: settings?.percentage,
                    datasets: deviceDataSet
                });

            }

        }
    }, [itemsChartData, locale]);

  const translateUnitLabel = useCallback((label, messages) => {
    switch (label) {
      case 'Hours':
        return messages['dashboardPanel_edit_hours'];
      default:
        return label;
    }
  }, []);

  const translatorLabel = useCallback((labels, messages) => {
    return labels.map(label => label.includes("Week") ? label.replace("Week", messages[`dashboardPanel_chart_label_Week`]) : messages[`dashboardPanel_chart_label_${label}`]);
  }, []);

  const percentageData = useCallback((chartData) => {
    let column = [];
    let response = [];
    let responseDevices = {};
    let total = 0;

    if (chartData.length > 1) {
      chartData.forEach((data) => {
        for (let i = 0; i < data?.data?.length; i++) {
          column[i] = column[i] ? column[i] + data.data[i] : data.data[i];
        }
      });
    } else {
      for (let i = 0; i < chartData[0]?.data?.length; i++) {
        total += chartData[0].data[i];
      }
    }

    chartData.forEach((data) => {
      let result = [];
      for (let i = 0; i < data?.data?.length; i++) {
        let totalColumn = (total > 0) ? total : column[i];
        result[i] = (data.data[i] == 0) ? 0 : ((data.data[i] / totalColumn) * 100);
        for (let property in data?.devices) {
          let percentageDevice = (data?.devices[property][i] == 0) ? 0 : ((data?.devices[property][i]  / totalColumn) * 100);
          if(!responseDevices[property])
            responseDevices[property]=[]

          responseDevices[property].push(percentageDevice)
        }
      }
      response.push({
        ...data,
        data: result,
        devices:responseDevices
      })
    });

    return response;
  }, []);

    //Get info for devices
    const viewData = (devices, settings) => {
        let deviceDataSet = [];
        for (const property in devices) {
            let groupName = unitDashboard.find((unit) => unit.id == property)?.label || property;
            let total = devices[property]?.reduce((tot, record) => tot + record, 0);

            if(charts_data.data){
                deviceDataSet.push({
                    stack: 1,
                    label: groupName,
                    total,
                    data: devices[property].slice(0, charts_data.data[0].label.length)
                })
            }
        }

        deviceDataSet = (settings.View.id == 1) ? deviceDataSet.slice().sort((a, b) => a.total < b.total ? 1 : -1) : deviceDataSet.slice().sort((a, b) => a.total > b.total ? 1 : -1);
        deviceDataSet = deviceDataSet.slice(0, settings.unitCount);
        deviceDataSet = deviceDataSet?.map((dev, key) => {
            return {
                ...dev,
                backgroundColor: backgroundColor[key]
            };
        });

        return deviceDataSet;
    }

    //Get average of chart
  const averageData = useCallback((chartData) => {
    let result = [];
    chartData.forEach(data => {
      for (let i = 0; i < data.data.length; i++) {
        result[i] = result[i] ? result[i] + data.data[i] : parseInt(data.data[i]);
      }
    });

    result = result.map(total => total / 2);

    chartData.unshift({
      label: messages['dashboardPanel_Average'],
      data: result,
      fill: false,
      backgroundColor: backgroundColor[1],
      borderColor: backgroundColor[1],
      pointBorderColor: backgroundColor[2],
      pointBorderWidth: 3,
      pointWidth: 13,
      borderDash: [7, 20],
      borderWidth: 4,
      type: 'line'
    });

    return chartData;
  }, []);

  return (
    <div style={{ textAlign: 'center', width: '100%', height: '100%', overflow: 'hidden' }}>
      {!data.hasOwnProperty('labels') && <Loading overlay show style={{ zIndex: 9 }} />}
      {typeChart === 'pie' ? <ChartPie data={data} /> : <ChartBar data={data} />}
    </div>
  );
}

export default Chart;
